/**
 * Theme context — single source of truth for per-user UI preferences.
 *
 * Lives client-side (localStorage); a backend persist hook can be
 * added later by swapping the storage layer. The provider applies the
 * preferences to ``document.documentElement`` and ``body`` so every
 * page picks them up via CSS without component-level wiring.
 *
 * Keys persisted under ``wms.theme.*``:
 *   themeMode             — 'light' | 'dark'
 *   fontSize              — 'small' | 'medium' | 'large'
 *   density               — 'comfortable' | 'compact'
 *   sidebarCollapsedByDefault — bool (initial state)
 *   darkSidebar           — bool
 *
 * Effects:
 *   - ``data-theme`` on <html>   → 'light' / 'dark' (CSS variables)
 *   - ``data-density`` on <html> → 'comfortable' / 'compact'
 *   - ``data-sidebar-dark`` on <html>
 *   - font-size on <html>        → 13px / 14px / 16px
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

const STORAGE_KEY = 'wms.theme';

const DEFAULTS = {
  themeMode: 'light',
  fontSize: 'medium',
  density: 'comfortable',
  sidebarCollapsedByDefault: false,
  darkSidebar: true,
};

const FONT_SIZE_PX = { small: '13px', medium: '14px', large: '16px' };

const readInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
};

const ThemeContext = createContext({
  ...DEFAULTS,
  setTheme: () => {},
  resetTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [state, setState] = useState(readInitial);

  // Apply preferences to <html> + <body> on mount and whenever they
  // change. Keeps every page in sync without needing to consume the
  // context in each one — CSS does the rest via data-* attributes.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', state.themeMode);
    root.setAttribute('data-density', state.density);
    root.setAttribute('data-sidebar-dark', state.darkSidebar ? 'true' : 'false');
    root.style.fontSize = FONT_SIZE_PX[state.fontSize] || FONT_SIZE_PX.medium;
    // body class so legacy CSS files (Bootstrap, custom) that key off
    // a class instead of data-* also pick up the mode.
    document.body.classList.toggle('theme-dark', state.themeMode === 'dark');
    document.body.classList.toggle('theme-light', state.themeMode !== 'dark');
    document.body.classList.toggle('density-compact', state.density === 'compact');
  }, [state]);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Quota exceeded / private mode — silently degrade; the
      // in-memory state still works for the current session.
    }
  }, []);

  const setTheme = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      persist(next);
      return next;
    });
  }, [persist]);

  const resetTheme = useCallback(() => {
    persist(DEFAULTS);
    setState(DEFAULTS);
  }, [persist]);

  const value = useMemo(
    () => ({ ...state, setTheme, resetTheme }),
    [state, setTheme, resetTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
